/**
 * SectionPill — Tier 0 slice-local primitive (SCRUM-167 epic).
 *
 * Sticky tablist that navigates between the 9 Property detail tabs
 * (Overview / Spaces / Units / Content / Pricing / Amenities /
 * Channels / Compliance / History) per
 * `~/Desktop/design_handoff_properties/PROPERTY_DETAIL_SPEC.md` §10.
 *
 * Renders as a `<div role="tablist">` per ACCESSIBILITY.md §2 +
 * WAI-ARIA Authoring Practices §3.27 (Tabs With Automatic
 * Activation), implementing the canonical roving tab-stop pattern:
 *
 *   - Only the currently-active tab has `tabIndex={0}`; others get
 *     `tabIndex={-1}`. The user Tabs into the pill exactly once and
 *     lands on the active tab.
 *   - Arrow keys move BOTH selection AND DOM focus to the next /
 *     previous tab. Home jumps to the first tab; End to the last.
 *   - Repeated arrow presses continue from the newly-focused tab,
 *     not from the stale index — because focus actually moves DOM-
 *     side, not just selection state.
 *
 * SCRUM-167 Codex blocker 1 fix (2026-05-28): earlier passes called
 * `onSelect(nextId)` without moving DOM focus, leaving repeated key
 * presses to continue from the wrong index. Now `tabRefs` tracks
 * each tab's button node; the keyboard handler focuses the new tab
 * in the same event tick after selection updates.
 *
 * The component is generic over the segment-id type so consumers
 * get exhaustive narrowing on `onSelect`. The sticky offset
 * (`top-[88px]`) matches the existing LeftMenu's pattern.
 */

import * as React from 'react';

export interface SectionPillSegment<TId extends string> {
  readonly id: TId;
  readonly label: string;
}

export interface SectionPillProps<TId extends string> {
  readonly segments: ReadonlyArray<SectionPillSegment<TId>>;
  readonly active: TId;
  readonly onSelect: (id: TId) => void;
  /** Accessible label for the tablist landmark. */
  readonly label: string;
}

export function SectionPill<TId extends string>({
  segments,
  active,
  onSelect,
  label,
}: SectionPillProps<TId>): React.ReactElement {
  // Refs into each tab button so the keyboard handler can move DOM
  // focus to the next tab in the same event tick as the selection
  // change. Without this, focus stays on the old tab and repeated
  // arrow presses continue from the stale index — the bug Codex
  // called out in the SCRUM-167 pre-review.
  const tabRefs = React.useRef<Array<HTMLButtonElement | null>>([]);

  // Arrow-key + Home/End navigation per ACCESSIBILITY.md §1.
  // Auto-activation tabs (per WAI-ARIA APG §3.27): arrows move BOTH
  // selection AND focus to the new tab.
  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLButtonElement>,
    currentIndex: number,
  ): void => {
    let nextIndex: number | null = null;
    if (e.key === 'ArrowLeft') {
      nextIndex = currentIndex === 0 ? segments.length - 1 : currentIndex - 1;
    } else if (e.key === 'ArrowRight') {
      nextIndex = (currentIndex + 1) % segments.length;
    } else if (e.key === 'Home') {
      nextIndex = 0;
    } else if (e.key === 'End') {
      nextIndex = segments.length - 1;
    }
    if (nextIndex !== null) {
      e.preventDefault();
      const segment = segments[nextIndex];
      if (segment !== undefined) {
        onSelect(segment.id);
        // Move DOM focus to the new tab so repeated arrow presses
        // continue from the right place. The ref is populated by
        // the render below; in the rare case it's null (the next
        // tab hasn't mounted yet — wouldn't happen during normal
        // user-driven nav) we skip the focus move and let React's
        // next render handle it.
        const nextTab = tabRefs.current[nextIndex];
        if (nextTab !== undefined && nextTab !== null) {
          nextTab.focus();
        }
      }
    }
  };

  return (
    <div
      role="tablist"
      aria-label={label}
      // The TopPill in DashboardPage is 64px + 24px gap. Total
      // 88px — matches LeftMenu's existing `top-[88px]` sticky
      // offset. The arbitrary-value pixel literal is unavoidable
      // because Tailwind's spacing steps don't include an 88px stop;
      // the existing LeftMenu has the same eslint-disable + audit-
      // exemption pattern at the same offset (NavLayout §16).
      // eslint-disable-next-line @sparta/no-pixel-literal-arbitrary -- SCRUM-167: sticky-offset 88px matches LeftMenu's top-[88px]; awaits SCRUM-77 follow-up branded `inset.88` token.
      className="sticky z-10 flex overflow-x-auto p-1 gap-0.5 rounded-full top-[88px]"
      style={{
        background: 'var(--bg-surface)',
        border: '1px solid var(--border-subtle)',
        boxShadow: 'var(--shadow-sm)',
      }}
    >
      {segments.map((seg, index) => {
        const isActive = seg.id === active;
        return (
          // eslint-disable-next-line @sparta/no-hand-rolled-button -- SCRUM-167: canonical WAI-ARIA tab — `role="tab"` + `aria-selected` + tabIndex semantics MUST live on a native <button> (per ACCESSIBILITY.md §2). Wrapping kit's <Button> would shadow the tab role.
          <button
            key={seg.id}
            ref={(el) => {
              tabRefs.current[index] = el;
            }}
            type="button"
            role="tab"
            aria-selected={isActive}
            tabIndex={isActive ? 0 : -1}
            onClick={() => onSelect(seg.id)}
            onKeyDown={(e) => handleKeyDown(e, index)}
            className="inline-flex items-center px-3.5 py-1.5 text-sm font-semibold rounded-full whitespace-nowrap transition-colors"
            style={{
              background: isActive ? 'var(--fg-primary)' : 'transparent',
              color: isActive
                ? 'var(--bg-surface)'
                : 'var(--fg-secondary)',
              border: 'none',
              cursor: 'pointer',
            }}
          >
            {seg.label}
          </button>
        );
      })}
    </div>
  );
}
